import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import SafeIcon from '../components/common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import {
  LIVE_SESSION_ESTADOS,
  LIVE_SESSION_TIPO_OPTIONS,
  claimLiveSessionSlot,
  completeLiveSessionSlot,
  createLiveSessionSlot,
  fetchLiveSessionZoomConfig,
  fetchUpcomingLiveSessionSlots,
  notifyAdminOfClaim,
  releaseLiveSessionSlot,
  upsertLiveSessionZoom,
} from '../services/liveSessions';

function toLocalDatetimeInputValue(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatSlotDateTime(iso) {
  return format(new Date(iso), "EEE d MMM yyyy · h:mm a", { locale: es });
}

function EstadoBadge({ estado }) {
  const config = LIVE_SESSION_ESTADOS[estado] ?? { label: estado, color: '' };
  const bgClass =
    estado === 'abierto'
      ? 'bg-brand-green/10 border-brand-green/30 text-brand-green'
      : estado === 'asignado'
        ? 'bg-brand-cyan/10 border-brand-cyan/30 text-brand-cyan'
        : 'bg-[var(--bg-base)] border-[var(--border-subtle)] text-[var(--text-secondary)]';

  return (
    <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${bgClass}`}>
      {config.label}
    </span>
  );
}

function SlotCard({
  slot,
  canClaim,
  canManage,
  onAction,
  actionLoading,
  isClaiming,
  claimForm,
  onStartClaim,
  onCancelClaim,
  onClaimFormChange,
  onSubmitClaim,
}) {
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <EstadoBadge estado={slot.estado} />
            <span className="text-[10px] font-black uppercase tracking-widest text-brand-gold">
              {slot.tipoLabel}
            </span>
          </div>

          <div>
            <p className="text-lg font-black text-[var(--text-main)] uppercase tracking-tight">
              {formatSlotDateTime(slot.scheduledAt)}
            </p>
            {slot.tema && (
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                <span className="font-bold uppercase text-[10px] tracking-widest mr-2">Tema</span>
                {slot.tema}
              </p>
            )}
          </div>

          {slot.entrenador && (
            <div className="rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)] p-4 space-y-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">
                Entrenador asignado
              </p>
              <p className="text-sm font-black text-[var(--text-main)]">{slot.entrenador.fullName}</p>
              <div className="flex flex-wrap gap-3 text-xs">
                {slot.entrenador.email && (
                  <a
                    href={`mailto:${slot.entrenador.email}`}
                    className="flex items-center gap-1.5 font-bold text-brand-cyan hover:underline"
                  >
                    <SafeIcon name="Mail" className="w-3.5 h-3.5" />
                    {slot.entrenador.email}
                  </a>
                )}
                {slot.entrenador.phone && (
                  <a
                    href={`tel:${slot.entrenador.phone}`}
                    className="flex items-center gap-1.5 font-bold text-brand-green hover:underline"
                  >
                    <SafeIcon name="Phone" className="w-3.5 h-3.5" />
                    {slot.entrenador.phone}
                  </a>
                )}
              </div>
            </div>
          )}

          {slot.estado !== 'abierto' && slot.zoomUrl && (
            <div className="rounded-xl bg-brand-navy/5 border border-brand-navy/20 p-4 space-y-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-brand-navy">
                Zoom{slot.zoomIsOverride ? ' (propio)' : ''}
              </p>
              <a
                href={slot.zoomUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold text-brand-cyan hover:underline break-all"
              >
                {slot.zoomUrl}
              </a>
              {slot.zoomPasscode && (
                <p className="text-xs text-[var(--text-secondary)]">
                  <span className="font-black uppercase tracking-widest">Contraseña:</span>{' '}
                  {slot.zoomPasscode}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 shrink-0 md:w-44">
          {slot.estado === 'abierto' && canClaim && !isClaiming && (
            <button
              type="button"
              disabled={actionLoading === slot.id}
              onClick={() => onStartClaim(slot)}
              className="w-full px-4 py-2.5 gfi-gradient-bg text-brand-navy rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm hover:opacity-90 disabled:opacity-50"
            >
              Reclamar cupo
            </button>
          )}

          {slot.estado === 'abierto' && canClaim && isClaiming && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onSubmitClaim(slot.id);
              }}
              className="w-full md:w-72 space-y-3 p-4 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)]"
            >
              <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">
                Detalles del cupo
              </p>
              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-1">
                  Tema (opcional)
                </label>
                <input
                  type="text"
                  value={claimForm.tema}
                  onChange={(e) => onClaimFormChange({ tema: e.target.value })}
                  placeholder="Ej. Bienvenida nuevos agentes"
                  className="w-full px-3 py-2 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-sm"
                />
              </div>
              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-1">
                  Zoom URL propio (opcional)
                </label>
                <input
                  type="url"
                  value={claimForm.zoomUrl}
                  onChange={(e) => onClaimFormChange({ zoomUrl: e.target.value })}
                  placeholder="https://zoom.us/j/..."
                  className="w-full px-3 py-2 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-sm"
                />
              </div>
              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-1">
                  Zoom passcode propio (opcional)
                </label>
                <input
                  type="text"
                  value={claimForm.zoomPasscode}
                  onChange={(e) => onClaimFormChange({ zoomPasscode: e.target.value })}
                  placeholder="Contraseña Zoom"
                  className="w-full px-3 py-2 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-sm"
                />
              </div>
              <p className="text-[9px] text-[var(--text-secondary)] leading-relaxed">
                Si dejas Zoom en blanco, se usará el enlace predeterminado del tipo de sesión.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  type="submit"
                  disabled={actionLoading === slot.id}
                  className="w-full px-4 py-2.5 gfi-gradient-bg text-brand-navy rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm hover:opacity-90 disabled:opacity-50"
                >
                  {actionLoading === slot.id ? 'Reclamando…' : 'Confirmar reclamo'}
                </button>
                <button
                  type="button"
                  disabled={actionLoading === slot.id}
                  onClick={onCancelClaim}
                  className="w-full px-4 py-2.5 border border-[var(--border-subtle)] text-[var(--text-secondary)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[var(--bg-surface)] disabled:opacity-50"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {canManage && slot.estado === 'asignado' && (
            <>
              <button
                type="button"
                disabled={actionLoading === slot.id}
                onClick={() => onAction('complete', slot.id)}
                className="w-full px-4 py-2.5 bg-brand-green text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-50"
              >
                {actionLoading === slot.id ? '…' : 'Marcar completado'}
              </button>
              <button
                type="button"
                disabled={actionLoading === slot.id}
                onClick={() => onAction('release', slot.id)}
                className="w-full px-4 py-2.5 border border-[var(--border-subtle)] text-[var(--text-secondary)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[var(--bg-base)] disabled:opacity-50"
              >
                Liberar cupo
              </button>
            </>
          )}

          {canManage && slot.estado === 'completado' && (
            <button
              type="button"
              disabled={actionLoading === slot.id}
              onClick={() => onAction('release', slot.id)}
              className="w-full px-4 py-2.5 border border-[var(--border-subtle)] text-[var(--text-secondary)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[var(--bg-base)] disabled:opacity-50"
            >
              Reabrir cupo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LiveSessionCalendarPage() {
  const { can } = useAuth();
  const canClaim = can('claim_live_session');
  const canManage = can('manage_live_sessions');

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState('all');

  const [createForm, setCreateForm] = useState({
    scheduledAt: '',
    tipo: 'onboarding',
    tema: '',
  });

  const [zoomForms, setZoomForms] = useState({
    onboarding: { zoomUrl: '', zoomPasscode: '' },
    entrenamiento_general: { zoomUrl: '', zoomPasscode: '' },
  });

  const [claimingSlotId, setClaimingSlotId] = useState(null);
  const [claimForm, setClaimForm] = useState({ tema: '', zoomUrl: '', zoomPasscode: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [slotRows, zoomRows] = await Promise.all([
        fetchUpcomingLiveSessionSlots({ includePast: canManage }),
        canManage ? fetchLiveSessionZoomConfig() : Promise.resolve([]),
      ]);
      setSlots(slotRows);
      if (canManage) {
        setZoomForms((prev) => {
          const next = { ...prev };
          for (const row of zoomRows) {
            next[row.tipo] = {
              zoomUrl: row.zoom_url ?? '',
              zoomPasscode: row.zoom_passcode ?? '',
            };
          }
          return next;
        });
      }
    } catch (err) {
      setError(err.message || 'Error al cargar el calendario');
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredSlots = useMemo(() => {
    if (estadoFilter === 'all') return slots;
    return slots.filter((s) => s.estado === estadoFilter);
  }, [slots, estadoFilter]);

  const handleCreateSlot = async (e) => {
    e.preventDefault();
    setActionLoading('create');
    setError('');
    setSuccess('');

    try {
      if (!createForm.scheduledAt) {
        throw new Error('Selecciona fecha y hora');
      }
      const scheduledAt = new Date(createForm.scheduledAt).toISOString();
      await createLiveSessionSlot({
        scheduledAt,
        tipo: createForm.tipo,
        tema: createForm.tema,
      });
      setCreateForm({ scheduledAt: '', tipo: 'onboarding', tema: '' });
      setSuccess('Cupo creado correctamente.');
      await loadData();
    } catch (err) {
      setError(err.message || 'No se pudo crear el cupo');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveZoom = async (tipo) => {
    setActionLoading(`zoom-${tipo}`);
    setError('');
    setSuccess('');

    try {
      const form = zoomForms[tipo];
      await upsertLiveSessionZoom({
        tipo,
        zoomUrl: form.zoomUrl,
        zoomPasscode: form.zoomPasscode,
      });
      setSuccess(`Zoom de ${LIVE_SESSION_TIPO_OPTIONS.find((t) => t.value === tipo)?.label} actualizado.`);
      await loadData();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la configuración Zoom');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartClaim = (slot) => {
    setClaimingSlotId(slot.id);
    setClaimForm({
      tema: slot.tema || '',
      zoomUrl: '',
      zoomPasscode: '',
    });
    setError('');
    setSuccess('');
  };

  const handleCancelClaim = () => {
    setClaimingSlotId(null);
    setClaimForm({ tema: '', zoomUrl: '', zoomPasscode: '' });
  };

  const handleClaimFormChange = (patch) => {
    setClaimForm((prev) => ({ ...prev, ...patch }));
  };

  const handleSubmitClaim = async (slotId) => {
    setActionLoading(slotId);
    setError('');
    setSuccess('');

    try {
      await claimLiveSessionSlot(slotId, claimForm);
      setClaimingSlotId(null);
      setClaimForm({ tema: '', zoomUrl: '', zoomPasscode: '' });
      try {
        await notifyAdminOfClaim(slotId);
        setSuccess('Cupo reclamado. Se notificó al admin.');
      } catch (notifyErr) {
        console.error(notifyErr);
        setSuccess('Cupo reclamado. La notificación al admin no se pudo enviar.');
      }
      await loadData();
    } catch (err) {
      setError(err.message || 'No se pudo reclamar el cupo');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSlotAction = async (action, slotId) => {
    setActionLoading(slotId);
    setError('');
    setSuccess('');

    try {
      if (action === 'complete') {
        await completeLiveSessionSlot(slotId);
        setSuccess('Sesión marcada como completada.');
      } else if (action === 'release') {
        if (!window.confirm('¿Liberar este cupo y volverlo a Abierto?')) {
          setActionLoading(null);
          return;
        }
        await releaseLiveSessionSlot(slotId);
        setSuccess('Cupo liberado.');
      }
      await loadData();
    } catch (err) {
      setError(err.message || 'Acción no completada');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-24 md:pb-12">
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-subtle)] pt-12 pb-8 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-black text-[var(--text-main)] uppercase italic tracking-tight mb-2 flex items-center gap-3">
            <SafeIcon name="Calendar" className="w-8 h-8 text-brand-cyan" />
            Calendario de Sesiones en Vivo
          </h1>
          <p className="text-xs text-[var(--text-secondary)] uppercase tracking-widest max-w-2xl">
            Agenda y reclama cupos de entrenamiento en vivo. Los datos de contacto del entrenador se toman de tu perfil automáticamente.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {canManage && (
          <button
            type="button"
            onClick={() => setShowAdmin((v) => !v)}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:underline"
          >
            <SafeIcon name={showAdmin ? 'ChevronUp' : 'ChevronDown'} className="w-4 h-4" />
            {showAdmin ? 'Ocultar panel admin' : 'Panel admin — crear cupos y Zoom'}
          </button>
        )}

        {canManage && showAdmin && (
          <div className="space-y-6">
            <form
              onSubmit={handleCreateSlot}
              className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-2xl p-6 space-y-4"
            >
              <h2 className="text-sm font-black uppercase tracking-[0.2em] text-[var(--text-main)]">
                Nuevo cupo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-2">
                    Fecha y hora
                  </label>
                  <input
                    type="datetime-local"
                    value={createForm.scheduledAt}
                    onChange={(e) => setCreateForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                    min={toLocalDatetimeInputValue(new Date())}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)] text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-2">
                    Tipo
                  </label>
                  <select
                    value={createForm.tipo}
                    onChange={(e) => setCreateForm((f) => ({ ...f, tipo: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)] text-sm"
                  >
                    {LIVE_SESSION_TIPO_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mb-2">
                  Tema (opcional)
                </label>
                <input
                  type="text"
                  value={createForm.tema}
                  onChange={(e) => setCreateForm((f) => ({ ...f, tema: e.target.value }))}
                  placeholder="Ej. Bienvenida nuevos agentes"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)] text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={actionLoading === 'create'}
                className="px-6 py-3 gfi-gradient-bg text-brand-navy rounded-xl text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
              >
                {actionLoading === 'create' ? 'Creando…' : 'Crear cupo'}
              </button>
            </form>

            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-2xl p-6 space-y-6">
              <h2 className="text-sm font-black uppercase tracking-[0.2em] text-[var(--text-main)]">
                Zoom por tipo de sesión
              </h2>
              {LIVE_SESSION_TIPO_OPTIONS.map((opt) => (
                <div key={opt.value} className="space-y-3 pb-4 border-b border-[var(--border-subtle)] last:border-0 last:pb-0">
                  <p className="text-xs font-black uppercase tracking-widest text-brand-gold">{opt.label}</p>
                  <input
                    type="url"
                    value={zoomForms[opt.value]?.zoomUrl ?? ''}
                    onChange={(e) =>
                      setZoomForms((f) => ({
                        ...f,
                        [opt.value]: { ...f[opt.value], zoomUrl: e.target.value },
                      }))
                    }
                    placeholder="https://zoom.us/j/..."
                    className="w-full px-4 py-3 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)] text-sm"
                  />
                  <input
                    type="text"
                    value={zoomForms[opt.value]?.zoomPasscode ?? ''}
                    onChange={(e) =>
                      setZoomForms((f) => ({
                        ...f,
                        [opt.value]: { ...f[opt.value], zoomPasscode: e.target.value },
                      }))
                    }
                    placeholder="Contraseña Zoom"
                    className="w-full px-4 py-3 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)] text-sm"
                  />
                  <button
                    type="button"
                    disabled={actionLoading === `zoom-${opt.value}`}
                    onClick={() => handleSaveZoom(opt.value)}
                    className="px-4 py-2 border border-brand-cyan text-brand-cyan rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-cyan/10 disabled:opacity-50"
                  >
                    Guardar {opt.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-600">
            {error}
          </div>
        )}
        {success && (
          <div className="p-4 rounded-xl bg-brand-green/10 border border-brand-green/30 text-sm text-brand-green">
            {success}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'Todos' },
            ...Object.values(LIVE_SESSION_ESTADOS),
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setEstadoFilter(opt.value)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-colors ${
                estadoFilter === opt.value
                  ? 'gfi-gradient-bg text-brand-navy border-transparent'
                  : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-[var(--text-secondary)]">Cargando cupos…</p>
        ) : filteredSlots.length === 0 ? (
          <div className="text-center py-16 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-2xl">
            <SafeIcon name="Calendar" className="w-10 h-10 text-[var(--text-secondary)] mx-auto mb-4" />
            <p className="text-sm text-[var(--text-secondary)]">No hay cupos programados.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSlots.map((slot) => (
              <SlotCard
                key={slot.id}
                slot={slot}
                canClaim={canClaim}
                canManage={canManage}
                onAction={handleSlotAction}
                actionLoading={actionLoading}
                isClaiming={claimingSlotId === slot.id}
                claimForm={claimForm}
                onStartClaim={handleStartClaim}
                onCancelClaim={handleCancelClaim}
                onClaimFormChange={handleClaimFormChange}
                onSubmitClaim={handleSubmitClaim}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
